"use client"

/**
 * 🔐 МНОЖЕСТВЕННАЯ АВТОРИЗАЦИЯ
 * Работа с несколькими аккаунтами одновременно
 */

import {
  login,
  getUser as getUserV3,
  type UserV3,
  type UserWorkspaceV3,
  type LoginResponse,
  ApiError,
} from '@/lib/api-v3'
import { debugLog, debugError } from '@/lib/debug'
import { syncMediaSession } from '@/lib/media-session'

// 📝 Типы для токенов
export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

/** Пользователь в приложении (совместимо с бывшим User из v1 API: role = systemPermission). */
export interface User {
  id: number
  name: string
  login: string
  role: number
  objectState?: number
  lastLogin?: string
  created?: string
  updated?: string
}

export type { LoginResponse }

/**
 * Нормализация ответа GET /user (v3) для кеша и проверок hasSystemAdminAccess.
 */
export function normalizeUserFromV3(user: UserV3): User {
  return {
    id: user.id,
    name: user.name,
    login: user.login,
    role: user.systemPermission ?? 0,
    objectState: user.objectState,
    lastLogin: user.lastLogin,
    created: user.created,
    updated: user.updated,
  }
}

/** Приводит объект из localStorage к User (v1 с role или v3 с systemPermission). */
export function normalizeStoredUser(raw: unknown): User {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid user data')
  }
  const o = raw as Record<string, unknown>
  if (typeof o.role === 'number') {
    return {
      id: o.id as number,
      name: String(o.name ?? ''),
      login: String(o.login ?? ''),
      role: o.role,
      objectState: o.objectState as number | undefined,
      lastLogin: o.lastLogin as string | undefined,
      created: o.created as string | undefined,
      updated: o.updated as string | undefined,
    }
  }
  return normalizeUserFromV3(raw as UserV3)
}

// 📝 Типы для множественных аккаунтов
export interface StoredAccount {
  id: string // уникальный ID (user.id + timestamp при создании)
  user: User
  tokens: AuthTokens
  lastUsed: number // timestamp последнего использования
}

// 🗄️ Ключи localStorage (новая структура)
const STORAGE_KEYS = {
  ACCOUNTS: "accounts",
  ACTIVE_ACCOUNT_ID: "activeAccountId",
  WORKSPACE_CACHE: "workspaceCache",
  // Старые ключи для миграции
  OLD_ACCESS_TOKEN: "accessToken",
  OLD_REFRESH_TOKEN: "refreshToken", 
  OLD_TOKEN_EXPIRES_AT: "tokenExpiresAt",
  OLD_USER_DATA: "userData",
} as const

/**
 * 🛠 РЕЖИМ РАЗРАБОТЧИКА (десктоп)
 * Ключ localStorage скрытого dev-режима. По умолчанию отсутствует — включается
 * только явно (5 кликов по заголовку на /users). Сбрасывается при каждом логине,
 * выходе и потере токена, чтобы не «переезжать» между сессиями и аккаунтами.
 */
export const DEV_MODE_STORAGE_KEY = "casper_dev_mode"

export const clearDevMode = (): void => {
  if (typeof window === "undefined") return
  if (localStorage.getItem(DEV_MODE_STORAGE_KEY) === null) return
  localStorage.removeItem(DEV_MODE_STORAGE_KEY)
  // Тот же сигнал, что использует переключатель на /users — подписчики (nav-user) обновятся сразу
  window.dispatchEvent(new Event("storage"))
}

/**
 * 🔄 МИГРАЦИЯ СТАРЫХ ДАННЫХ
 */
// Флаг для отслеживания, что миграция уже проверена в текущей сессии
let migrationChecked = false

const migrateOldDataIfNeeded = (): void => {
  if (typeof window === "undefined") return
  
  // Если уже проверяли в этой сессии, пропускаем
  if (migrationChecked) return
  migrationChecked = true

  try {
    // Проверяем, есть ли уже новая структура
    const existingAccounts = localStorage.getItem(STORAGE_KEYS.ACCOUNTS)
    if (existingAccounts) {
      // Новая структура уже есть, миграция не нужна (не логируем, чтобы не спамить)
      return
    }

    // Проверяем наличие старых данных
    const oldAccessToken = localStorage.getItem(STORAGE_KEYS.OLD_ACCESS_TOKEN)
    const oldRefreshToken = localStorage.getItem(STORAGE_KEYS.OLD_REFRESH_TOKEN)
    const oldExpiresAt = localStorage.getItem(STORAGE_KEYS.OLD_TOKEN_EXPIRES_AT)
    const oldUserData = localStorage.getItem(STORAGE_KEYS.OLD_USER_DATA)

    if (!oldAccessToken || !oldRefreshToken || !oldExpiresAt || !oldUserData) {
      // Нет старых данных для миграции (не логируем, это нормально)
      return
    }

    debugLog('auth', "🔄 Начинаем миграцию старых данных...")

    // Парсим старые данные (v1 или v3)
    const user = normalizeStoredUser(JSON.parse(oldUserData))
    const tokens: AuthTokens = {
      accessToken: oldAccessToken,
      refreshToken: oldRefreshToken,
      expiresAt: parseInt(oldExpiresAt, 10),
    }

    // Создаем первый аккаунт из старых данных
    const account: StoredAccount = {
      id: `${user.id}_${Date.now()}`,
      user,
      tokens,
      lastUsed: Date.now(),
    }

    // Сохраняем в новой структуре
    localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify([account]))
    localStorage.setItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID, account.id)

    // Удаляем старые ключи
    localStorage.removeItem(STORAGE_KEYS.OLD_ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.OLD_REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.OLD_TOKEN_EXPIRES_AT)
    localStorage.removeItem(STORAGE_KEYS.OLD_USER_DATA)

    debugLog('auth', "✅ Миграция завершена успешно")
    debugLog('auth', `📦 Создан аккаунт: ${user.name} (${user.login})`)
  } catch (error) {
    debugError("❌ Ошибка миграции:", error)
  }
}

/**
 * 📦 РАБОТА С АККАУНТАМИ
 */

export const getAllAccounts = (): StoredAccount[] => {
  if (typeof window === "undefined") return []

  try {
    migrateOldDataIfNeeded()

    const accountsStr = localStorage.getItem(STORAGE_KEYS.ACCOUNTS)
    if (!accountsStr) return []

    const accounts: StoredAccount[] = JSON.parse(accountsStr).map((acc: StoredAccount) => ({
      ...acc,
      user: normalizeStoredUser(acc.user as unknown),
    }))
    
    // Сортируем по алфавиту (по имени пользователя)
    return accounts.sort((a, b) => a.user.name.localeCompare(b.user.name, 'ru'))
  } catch (error) {
    debugError("❌ Ошибка получения аккаунтов:", error)
    return []
  }
}

export const getActiveAccount = (): StoredAccount | null => {
  if (typeof window === "undefined") return null

  try {
    migrateOldDataIfNeeded()

    const activeId = localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID)
    if (!activeId) return null

    const accounts = getAllAccounts()
    const account = accounts.find(acc => acc.id === activeId)

    if (!account) {
      debugLog('auth', "⚠️ Активный аккаунт не найден, выбираем первый")
      // Если активный аккаунт не найден, выбираем первый доступный
      if (accounts.length > 0) {
        switchAccount(accounts[0].id)
        return accounts[0]
      }
      return null
    }

    return account
  } catch (error) {
    debugError("❌ Ошибка получения активного аккаунта:", error)
    return null
  }
}

export const saveAccount = (user: User, tokens: AuthTokens): string => {
  if (typeof window === "undefined") return ""

  try {
    migrateOldDataIfNeeded()

    const accounts = getAllAccounts()
    
    // Проверяем, есть ли уже аккаунт с таким user.id
    const existingAccount = accounts.find(acc => acc.user.id === user.id)

    if (existingAccount) {
      // Обновляем существующий аккаунт
      debugLog('auth', `🔄 Обновление аккаунта: ${user.name}`)
      existingAccount.user = user
      existingAccount.tokens = tokens
      existingAccount.lastUsed = Date.now()
      
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent('casper:accounts-updated'))
      }

      return existingAccount.id
    } else {
      // Создаем новый аккаунт
      debugLog('auth', `➕ Добавление нового аккаунта: ${user.name}`)
      const newAccount: StoredAccount = {
        id: `${user.id}_${Date.now()}`,
        user,
        tokens,
        lastUsed: Date.now(),
      }
      
      accounts.push(newAccount)
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent('casper:accounts-updated'))
      }

      return newAccount.id
    }
  } catch (error) {
    debugError("❌ Ошибка сохранения аккаунта:", error)
    return ""
  }
}

export const switchAccount = (accountId: string): boolean => {
  if (typeof window === "undefined") return false

  try {
    const accounts = getAllAccounts()
    const account = accounts.find(acc => acc.id === accountId)

    if (!account) {
      debugError("❌ Аккаунт не найден:", accountId)
      return false
    }

    // Обновляем lastUsed
    account.lastUsed = Date.now()
    localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))

    // Устанавливаем как активный
    localStorage.setItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID, accountId)
    debugLog('auth', `✅ Переключились на аккаунт: ${account.user.name}`)

    // Новая сессия (логин или смена аккаунта) — dev-режим сбрасывается
    clearDevMode()
    syncMediaSessionWithActiveAccount()

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent('casper:accounts-updated'))
    }

    return true
  } catch (error) {
    debugError("❌ Ошибка переключения аккаунта:", error)
    return false
  }
}

export const removeAccount = (accountId: string): void => {
  if (typeof window === "undefined") return

  try {
    const accounts = getAllAccounts()
    const accountIndex = accounts.findIndex(acc => acc.id === accountId)

    if (accountIndex === -1) {
      debugError("❌ Аккаунт не найден для удаления:", accountId)
      return
    }

    const accountName = accounts[accountIndex].user.name
    accounts.splice(accountIndex, 1)

    if (accounts.length === 0) {
      // Если это был последний аккаунт, очищаем все
      debugLog('auth', "🗑️ Удален последний аккаунт, очистка...")
      clearAllAccounts()
      if (typeof window !== "undefined") {
        window.location.href = getLoginPath()
      }
      return
    }

    // Сохраняем обновленный список
    localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))

    // Если удалили активный аккаунт, переключаемся на первый
    const activeId = localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID)
    if (activeId === accountId) {
      debugLog('auth', "🔄 Удален активный аккаунт, переключаемся на первый")
      switchAccount(accounts[0].id)
    }

    debugLog('auth', `🗑️ Аккаунт удален: ${accountName}`)
  } catch (error) {
    debugError("❌ Ошибка удаления аккаунта:", error)
  }
}

export const clearAllAccounts = (): void => {
  if (typeof window === "undefined") return
  
  localStorage.removeItem(STORAGE_KEYS.ACCOUNTS)
  localStorage.removeItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID)
  clearDevMode()
  syncMediaSession(null)
  
  debugLog('auth', "🗑️ Все аккаунты удалены")
}

/** Очищает токены конкретного аккаунта, не удаляя его из списка. */
export const clearAccountTokens = (accountId: string): void => {
  if (typeof window === "undefined") return
  try {
    const accounts = getAllAccounts()
    const idx = accounts.findIndex(acc => acc.id === accountId)
    if (idx === -1) return
    accounts[idx].tokens = { accessToken: "", refreshToken: "", expiresAt: 0 }
    localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))
    syncMediaSessionWithActiveAccount()
    debugLog('auth', `🔒 Токены очищены: ${accounts[idx].user.login}`)
  } catch (error) {
    debugError("❌ Ошибка очистки токенов аккаунта:", error)
  }
}

/** Очищает токены всех неактивных аккаунтов (только логины остаются). */
export const clearAllInactiveTokens = (): void => {
  if (typeof window === "undefined") return
  try {
    const activeId = localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT_ID)
    const accounts = getAllAccounts()
    let changed = false
    for (const acc of accounts) {
      if (acc.id !== activeId && (acc.tokens.accessToken || acc.tokens.refreshToken)) {
        acc.tokens = { accessToken: "", refreshToken: "", expiresAt: 0 }
        changed = true
        debugLog('auth', `🔒 Очищены токены неактивного аккаунта: ${acc.user.login}`)
      }
    }
    if (changed) {
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))
    }
  } catch (error) {
    debugError("❌ Ошибка очистки токенов неактивных аккаунтов:", error)
  }
}

export const findAccountByUserId = (userId: number): StoredAccount | null => {
  const accounts = getAllAccounts()
  return accounts.find(acc => acc.user.id === userId) || null
}

/**
 * 🔑 ТОКЕНЫ (для активного аккаунта)
 */
export const getTokens = (): AuthTokens | null => {
  const account = getActiveAccount()
  return account?.tokens || null
}

export const saveTokens = (tokens: LoginResponse, user?: User): void => {
  if (typeof window === "undefined") return

  try {
    // expiresIn в секундах (OAuth-стиль)
    const expiresAt = Date.now() + tokens.expiresIn * 1000
    const authTokens: AuthTokens = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt,
    }

    // Если пользователь передан — это логин (а не refresh): сохраняем/обновляем аккаунт
    // и сбрасываем dev-режим, чтобы он не переезжал в новую сессию
    if (user) {
      clearDevMode()
      const accountId = saveAccount(user, authTokens)
      switchAccount(accountId)
    } else {
      // Обновляем токены активного аккаунта
      const account = getActiveAccount()
      if (account) {
        account.tokens = authTokens
        account.lastUsed = Date.now()
        
        const accounts = getAllAccounts()
        const index = accounts.findIndex(acc => acc.id === account.id)
        if (index !== -1) {
          accounts[index] = account
          localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))
        }
      }
    }
    syncMediaSessionWithActiveAccount()
    
    // Вычисляем время жизни токена
    const hours = Math.floor(tokens.expiresIn / 3600)
    const minutes = Math.floor((tokens.expiresIn % 3600) / 60)
    const seconds = tokens.expiresIn % 60
    
    const expirationDate = new Date(expiresAt)
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    
    debugLog('auth', "✅ Токены сохранены")
    debugLog('auth', `⏰ Время жизни токена: ${timeString} (до ${expirationDate.toLocaleString('ru-RU')})`)
  } catch (error) {
    debugError("❌ Ошибка сохранения токенов:", error)
  }
}

export const clearTokens = (): void => {
  // Потеря токена (истёк / 401) — dev-режим не должен пережить сессию
  clearDevMode()
  const account = getActiveAccount()
  if (account) {
    account.tokens.accessToken = ""
    account.tokens.refreshToken = ""
    account.tokens.expiresAt = 0
    
    const accounts = getAllAccounts()
    const index = accounts.findIndex(acc => acc.id === account.id)
    if (index !== -1) {
      accounts[index] = account
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))
    }
  }
  syncMediaSession(null)

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent('casper:auth-invalidated'))
  }
}

/**
 * 👤 ПОЛЬЗОВАТЕЛЬ (активного аккаунта)
 */
export const saveUserData = (user: User): void => {
  // Для обратной совместимости - обновляем данные активного аккаунта
  const account = getActiveAccount()
  if (account) {
    account.user = user
    account.lastUsed = Date.now()
    
    const accounts = getAllAccounts()
    const index = accounts.findIndex(acc => acc.id === account.id)
    if (index !== -1) {
      accounts[index] = account
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts))
      debugLog('auth', "✅ Пользователь обновлен:", user.name)
    }
  }
}

export const getUserData = (): User | null => {
  const account = getActiveAccount()
  return account?.user || null
}

export const clearUserData = (): void => {
  // Для обратной совместимости - удаляем активный аккаунт
  clearTokens()
}

/**
 * 🏢 КЭШИРОВАННЫЙ WORKSPACE (по accountId)
 */
export const saveWorkspaceForAccount = (accountId: string, workspace: UserWorkspaceV3): void => {
  if (typeof window === "undefined") return
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.WORKSPACE_CACHE)
    const cache: Record<string, UserWorkspaceV3> = raw ? JSON.parse(raw) : {}
    cache[accountId] = workspace
    localStorage.setItem(STORAGE_KEYS.WORKSPACE_CACHE, JSON.stringify(cache))
  } catch (error) {
    debugError("❌ Ошибка сохранения workspace кэша:", error)
  }
}

export const getWorkspaceForAccount = (accountId: string): UserWorkspaceV3 | null => {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.WORKSPACE_CACHE)
    if (!raw) return null
    const cache: Record<string, UserWorkspaceV3> = JSON.parse(raw)
    return cache[accountId] ?? null
  } catch (error) {
    debugError("❌ Ошибка чтения workspace кэша:", error)
    return null
  }
}

export const clearWorkspaceForAccount = (accountId: string): void => {
  if (typeof window === "undefined") return
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.WORKSPACE_CACHE)
    if (!raw) return
    const cache: Record<string, UserWorkspaceV3> = JSON.parse(raw)
    delete cache[accountId]
    localStorage.setItem(STORAGE_KEYS.WORKSPACE_CACHE, JSON.stringify(cache))
  } catch (error) {
    debugError("❌ Ошибка очистки workspace кэша:", error)
  }
}

/**
 * ✅ ПРОВЕРКИ
 */
export const isTokenExpired = (expiresAt: number): boolean => {
  return Date.now() >= expiresAt
}

/** Кука медиа-сессии (/api/media/session) — по токену активного аккаунта; не блокирует и не бросает. */
export const syncMediaSessionWithActiveAccount = (): void => {
  if (typeof window === "undefined") return
  const tokens = getActiveAccount()?.tokens
  syncMediaSession(tokens?.accessToken && !isTokenExpired(tokens.expiresAt) ? tokens.accessToken : null)
}

export const getTokenTimeRemaining = (): string | null => {
  const tokens = getTokens()
  if (!tokens) return null
  
  const remainingMs = tokens.expiresAt - Date.now()
  if (remainingMs <= 0) return "Истек"
  
  const remainingSeconds = Math.floor(remainingMs / 1000)
  const hours = Math.floor(remainingSeconds / 3600)
  const minutes = Math.floor((remainingSeconds % 3600) / 60)
  const seconds = remainingSeconds % 60
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export const isAuthenticated = (): boolean => {
  const tokens = getTokens()
  if (!tokens || !tokens.accessToken) return false
  
  if (isTokenExpired(tokens.expiresAt)) {
    debugLog('auth', "⏰ Токен истек")
    clearTokens()
    return false
  }
  
  return true
}

/**
 * 👤 ПОЛУЧЕНИЕ ПОЛЬЗОВАТЕЛЯ С СЕРВЕРА
 */
export const fetchUserData = async (): Promise<User | null> => {
  const tokens = getTokens()
  if (!tokens || isTokenExpired(tokens.expiresAt)) {
    debugLog('auth', "❌ Нет валидного токена")
    return null
  }

  try {
    debugLog('auth', "👤 Получаем пользователя...")
    const userData = normalizeUserFromV3(await getUserV3(tokens.accessToken))
    // НЕ сохраняем автоматически - это может создать дубликаты
    // Вызывающий код сам решит, сохранять или нет
    return userData
  } catch (error) {
    debugError("❌ Ошибка получения пользователя:", error)
    
    // При 401 сбрасываем токен и перекидываем на логин (без удаления аккаунта)
    if (error instanceof ApiError && error.status === 401) {
      debugLog('auth', "❌ 401 Unauthorized - сброс токена и редирект")
      clearTokens()
      if (typeof window !== "undefined") {
        window.location.href = getLoginPath()
      }
    }
    
    return null
  }
}

/**
 * 🚪 ВЫХОД
 */
export const logout = (): void => {
  debugLog('auth', "🚪 Выход из активного аккаунта...")
  clearDevMode()
  const account = getActiveAccount()
  if (account) {
    removeAccount(account.id)
  }
  
  // Проверяем, остались ли другие аккаунты
  const remainingAccounts = getAllAccounts()
  if (remainingAccounts.length === 0 && typeof window !== "undefined") {
    window.location.href = getLoginPath()
  }
}

export const logoutAll = (): void => {
  debugLog('auth', "🚪 Выход из всех аккаунтов...")
  clearDevMode()
  clearAllAccounts()
  if (typeof window !== "undefined") {
    window.location.href = getLoginPath()
  }
}

/**
 * 🔗 ПУТЬ К СТРАНИЦЕ ВХОДА
 * Возвращает /mobile/login если мы на мобильном маршруте, иначе /login.
 */
export const getLoginPath = (): string => {
  if (typeof window === "undefined") return "/login"
  return window.location.pathname.startsWith("/mobile") ? "/mobile/login" : "/login"
}

/**
 * 🔍 ПРОВЕРКА ПРИ ЗАГРУЗКЕ
 */
export const checkAuthOnLoad = () => {
  migrateOldDataIfNeeded()
  
  const tokens = getTokens()
  const user = getUserData()
  const isAuth = isAuthenticated()
  
  debugLog('auth', `🔍 ${isAuth ? '✅ Авторизован' : '❌ Не авторизован'}`)
  
  if (isAuth && tokens) {
    const timeRemaining = getTokenTimeRemaining()
    if (timeRemaining) {
      debugLog('auth', `⏰ При загрузке: токен действует еще ${timeRemaining}`)
    }
  }
  
  return {
    isAuthenticated: isAuth,
    tokens,
    user,
  }
}

/**
 * 👤 ПОЛУЧЕНИЕ ПОЛЬЗОВАТЕЛЯ (кеш или сервер)
 */
export const getCurrentUser = async (forceRefresh: boolean = false): Promise<User | null> => {
  if (!isAuthenticated()) {
    debugLog('auth', "❌ Не авторизован")
    return null
  }

  if (!forceRefresh) {
    const cachedUser = getUserData()
    if (cachedUser) {
      debugLog('auth', "📱 Из кеша:", cachedUser.name)
      return cachedUser
    }
  }

  debugLog('auth', "🌐 С сервера...")
  return await fetchUserData()
}
